import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {SpinnerService} from "../../../utils/services/spinner.service";
import {EMPTY, filter, interval, merge, Observable, Subscription, switchMap, tap} from "rxjs";
import {WebSocketService} from '../../services/web-socket.service';
import {environment} from '../../../../environments/environment';
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import * as LessonsV3 from '../../model/v3/lessons';

@Component({
    selector: 'app-student-lesson-details-page',
    templateUrl: './student-lesson-details-page.component.html',
    styleUrls: ['./student-lesson-details-page.component.scss'],
    standalone: false
})
export class StudentLessonDetailsPageComponent implements OnInit, OnDestroy {
  studentId: number;
  lessonId: number;
  lesson: LessonsV3.LFStudentGeneral;
  private detailsSubscription: Subscription;
  private wsSubscription: Subscription;
  private pollingSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private apiProvider: NativeServiceApiProvider,
    private spinner: SpinnerService,
    private webSocket: WebSocketService,
  ) {}

  ngOnInit(): void {
    this.detailsSubscription = this.route.paramMap.pipe(
      tap((params) => {
        this.studentId = Number(params.get('studentId'));
        this.lessonId = Number(params.get('lessonId'));
      }),
      tap(() => this.listenToLessonUpdates()),
      tap(() => this.startPollingFallback()),
      switchMap(() => this.loadLessonDetails())
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.detailsSubscription?.unsubscribe();
    this.wsSubscription?.unsubscribe();
    this.pollingSubscription?.unsubscribe();
  }

  private listenToLessonUpdates(): void {
    if (!this.studentId) {
      return;
    }

    this.wsSubscription?.unsubscribe();
    this.wsSubscription = this.webSocket.establish(environment.eventsWsEndpoint).pipe(
      filter((connection) => connection?.client?.connected),
      switchMap((connection) => merge(
        connection.subscribe(`/col/student/${this.studentId}/LessonListUpdate`),
        connection.subscribe(`/col/student/${this.studentId}/LessonUpdate`)
      )),
      switchMap(() => this.loadLessonDetails())
    ).subscribe();
  }

  private startPollingFallback(): void {
    if (!this.studentId || !this.lessonId) {
      return;
    }

    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = interval(60_000).pipe(
      switchMap(() => this.loadLessonDetails())
    ).subscribe();
  }

  private loadLessonDetails(): Observable<LessonsV3.LessonView> {
    if (!this.lessonId || !this.studentId) {
      return EMPTY as Observable<LessonsV3.LessonView>;
    }
    return this.spinner.trace(
      this.apiProvider
        .colV3()
        .pipe(
          switchMap((api) => api.getLesson(this.lessonId, 'student-full', this.studentId)),
          tap((lesson) => (this.lesson = lesson as LessonsV3.LFStudentGeneral))
        )
    );
  }

  isProva(): boolean {
    return this.lesson?.type === LessonsV3.LessonType.Prova;
  }

  isKidsEnglish(): boolean {
    const lesson = this.lesson;
    if (!lesson || !('course' in lesson) || !lesson.course) {
      return false;
    }
    return lesson.course.code?.startsWith('en.ch.') ?? false;
  }
}
